import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { TokenFrame } from '@/pages/TokenPages'
import { useSession, useSignOut } from '@/features/auth/mutations'
import { useMcpDecision, useMcpPreview } from '@/features/mcp/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/**
 * "Allow this app?" — the consent step of connecting an MCP client.
 *
 * ── How anyone gets here ────────────────────────────────────────────────
 * Only by redirect. Someone adds ForgeAI as a connector in Claude, Claude opens
 * the backend's authorize endpoint, and the backend sends the browser to
 * `/app/mcp-authorize?token=…`. Nothing in the app links here.
 *
 * ── Inside RequireAuth, unlike the other token screens ──────────────────
 * Approving is acting as the signed-in user, so there must be one. A
 * signed-out visitor goes to sign-in and comes back with the query string
 * intact, the same round trip any guarded deep link takes.
 *
 * ── The token is a secret ───────────────────────────────────────────────
 * It is read from the URL, sent in POST bodies and nowhere else: not rendered,
 * not logged, not in a query key.
 *
 * ── Leaving is a full navigation ────────────────────────────────────────
 * Either answer returns a URL on another site, or on a local app's loopback
 * listener. The router cannot go there, so it is `window.location.assign`, and
 * straight away: the code inside it lasts sixty seconds.
 */

/**
 * Hosts on this machine. A request to one of these is an app running locally,
 * which is legitimate for Claude Code and also what anything else running
 * locally would look like.
 */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]'])

/**
 * The URL comes from our own backend, but it is handed straight to the browser,
 * so a script scheme is refused rather than trusted. Custom schemes stay
 * allowed — a desktop client may register one for its callback.
 */
const REFUSED_SCHEMES = new Set(['javascript:', 'data:', 'vbscript:'])

function isNavigable(url) {
  try {
    return !REFUSED_SCHEMES.has(new URL(url).protocol)
  } catch {
    return false
  }
}

/** Whole minutes left, rounded up, re-read every fifteen seconds. Null once expired. */
function useMinutesLeft(expiresAt) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!expiresAt) return undefined
    const id = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(id)
  }, [expiresAt])

  if (!expiresAt) return undefined
  const ms = new Date(expiresAt).getTime() - now
  return ms > 0 ? Math.ceil(ms / 60_000) : null
}

function Notice({ tone, role = 'status', children, ...rest }) {
  const tones = {
    red: 'stripe-red text-on-red',
    amber: 'stripe-amber text-on-amber',
    green: 'stripe-green text-on-green',
  }
  return (
    <p
      role={role}
      className={`m-0 rounded-sm border border-hairline bg-card p-3.5 text-body-sm ${tones[tone]}`}
      {...rest}
    >
      {children}
    </p>
  )
}

export function McpAuthorizePage() {
  const { t } = useTranslation(['auth', 'common'])
  useDocumentTitle(t('mcpAuthorize.meta.title'), t('mcpAuthorize.meta.description'))

  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const location = useLocation()
  const navigate = useNavigate()

  const { data: session } = useSession()
  const signOut = useSignOut()
  const preview = useMcpPreview(token)
  const decision = useMcpDecision()
  const minutesLeft = useMinutesLeft(preview.data?.expiresAt)

  const [left, setLeft] = useState(false)
  const [unusable, setUnusable] = useState(false)
  const errorRef = useRef(null)

  useEffect(() => {
    if (decision.isError) errorRef.current?.focus()
  }, [decision.isError])

  function decide(approve) {
    decision.mutate(
      { token, approve },
      {
        onSuccess: ({ redirectUrl }) => {
          // The token is spent either way, so a missing or refused URL leaves
          // nothing to retry from here.
          if (!redirectUrl || !isNavigable(redirectUrl)) {
            setUnusable(true)
            return
          }
          setLeft(true)
          window.location.assign(redirectUrl)
        },
      },
    )
  }

  function handleSignOut() {
    // Back to sign-in with this request as the destination, so a different
    // person can sign in and land on the same consent screen.
    signOut.mutate(undefined, {
      onSettled: () =>
        navigate('/login', { replace: true, state: { from: location.pathname + location.search } }),
    })
  }

  const heading = t('mcpAuthorize.heading')
  const intro = t('mcpAuthorize.intro')

  if (left) {
    return (
      <TokenFrame heading={t('mcpAuthorize.doneHeading')} intro={t('mcpAuthorize.doneIntro')}>
        <p className="m-0 text-body-sm text-ink">{t('mcpAuthorize.closeTab')}</p>
      </TokenFrame>
    )
  }

  const tokenRejected =
    !token ||
    preview.error?.fields?.token ||
    decision.error?.fields?.token ||
    minutesLeft === null ||
    unusable

  if (tokenRejected) {
    return (
      <TokenFrame heading={heading} intro={intro}>
        <Notice tone="red" role="alert">{t('mcpAuthorize.errors.invalid')}</Notice>
      </TokenFrame>
    )
  }

  if (preview.isError) {
    return (
      <TokenFrame heading={heading} intro={intro}>
        <Notice tone="red" role="alert">{t('common:errors.unknown')}</Notice>
      </TokenFrame>
    )
  }

  if (preview.isPending) {
    return (
      <TokenFrame heading={heading} intro={intro}>
        <p role="status" className="m-0 text-body-sm text-ink-dim">{t('mcpAuthorize.loading')}</p>
      </TokenFrame>
    )
  }

  const { clientName, redirectHost, scopeDescription } = preview.data
  const loopback = LOOPBACK_HOSTS.has(redirectHost.toLowerCase())
  const busy = decision.isPending || decision.isSuccess || signOut.isPending
  const user = session?.user
  const account = session?.account

  return (
    <TokenFrame heading={heading} intro={intro}>
      <div className="flex flex-col gap-4.5">
        {decision.isError ? (
          <Notice tone="red" role="alert" ref={errorRef} tabIndex={-1}>
            {t('common:errors.unknown')}
          </Notice>
        ) : null}

        <div className="flex flex-col gap-1">
          <p className="m-0 text-headline text-ink">{clientName}</p>
          <p className="m-0 text-body-sm text-ink-dim">
            {t('mcpAuthorize.sendsTo')} <span className="mono text-ink">{redirectHost}</span>
          </p>
        </div>

        {loopback ? <Notice tone="amber">{t('mcpAuthorize.loopbackWarning')}</Notice> : null}

        <div className="flex flex-col gap-1">
          <p className="m-0 text-label text-ink-mute">{t('mcpAuthorize.scopeLabel')}</p>
          <p className="m-0 text-body-sm text-ink">{scopeDescription}</p>
        </div>

        <div className="flex flex-col gap-1">
          <p className="m-0 text-label text-ink-mute">{t('mcpAuthorize.signedInAs')}</p>
          <p className="m-0 text-body-sm text-ink">
            {t('mcpAuthorize.who', { name: user?.name ?? '—', account: account?.name ?? '—' })}
          </p>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={busy}
            className="self-start text-body-sm text-ink-dim underline underline-offset-2 hover:text-ink disabled:opacity-60"
          >
            {t('mcpAuthorize.notYou')}
          </button>
        </div>

        <p className="m-0 text-body-sm text-ink-dim">
          {t('mcpAuthorize.expiresIn', { count: minutesLeft })}
        </p>
        <p className="m-0 text-body-sm text-ink-dim">{t('mcpAuthorize.noRevoke')}</p>

        <div className="flex gap-3">
          <ForgeButton variant="secondary" size="lg" className="flex-1" disabled={busy} onClick={() => decide(false)}>
            {t('mcpAuthorize.deny')}
          </ForgeButton>
          <ForgeButton variant="primary" size="lg" className="flex-1" disabled={busy} onClick={() => decide(true)}>
            {decision.isPending && decision.variables?.approve
              ? t('mcpAuthorize.allowing')
              : t('mcpAuthorize.allow')}
          </ForgeButton>
        </div>
      </div>
    </TokenFrame>
  )
}
